import { useState, useCallback } from 'react';

import { AssessmentData, SMSAutoResponse } from 'domains/content/types';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

import { SmsSaveData, SmsSaveMode } from './useSmsSubmission';

type UseAssessmentHandlingProps = {
    initialAssessmentData: AssessmentData;
    isNameTooShort: boolean;
    isUpdate: boolean;
    handleSave: (saveData: SmsSaveData) => void;
};

const useAssessmentHandling = ({
    initialAssessmentData,
    isNameTooShort,
    isUpdate,
    handleSave,
}: UseAssessmentHandlingProps) => {
    const [assessmentData, setAssessmentData] = useState<AssessmentData>({
        assessmentId: initialAssessmentData?.assessmentId || '',
        assessmentName: initialAssessmentData?.assessmentName || '',
        assessmentConsentRequired: initialAssessmentData?.assessmentConsentRequired ?? true,
    });

    const handleSaveWithAssessment = useCallback(
        ({
            name,
            content,
            saveMode,
            overrideAssessmentData,
            autoResponses,
            status,
            contentMultilingual,
            autoResponsesMultilingual,
        }: {
            name: string;
            content: string;
            saveMode: SmsSaveMode;
            overrideAssessmentData?: AssessmentData;
            autoResponses?: SMSAutoResponse[];
            status?: ContentStatus;
            contentMultilingual?: Record<string, string>;
            autoResponsesMultilingual?: Record<string, SMSAutoResponse[]>;
        }) => {
            const dataToSave = overrideAssessmentData || assessmentData;

            handleSave({
                name,
                content,
                isNameTooShort,
                isUpdate,
                saveMode,
                assessmentData: dataToSave,
                autoResponses,
                status,
                contentMultilingual,
                autoResponsesMultilingual,
            });
        },
        [assessmentData, handleSave, isNameTooShort, isUpdate]
    );

    const handleSaveAndExitWithAssessment = useCallback(
        ({
            name,
            content,
            overrideAssessmentData,
            autoResponses,
            status,
            contentMultilingual,
            autoResponsesMultilingual,
        }: {
            name: string;
            content: string;
            overrideAssessmentData?: AssessmentData;
            autoResponses?: SMSAutoResponse[];
            status?: ContentStatus;
            contentMultilingual?: Record<string, string>;
            autoResponsesMultilingual?: Record<string, SMSAutoResponse[]>;
        }) =>
            handleSaveWithAssessment({
                name,
                content,
                saveMode: SmsSaveMode.EXIT,
                overrideAssessmentData,
                autoResponses,
                status,
                contentMultilingual,
                autoResponsesMultilingual,
            }),
        [handleSaveWithAssessment]
    );

    const handleSaveNameWithAssessment = useCallback(
        ({
            name,
            content,
            overrideAssessmentData,
            autoResponses,
            contentMultilingual,
            autoResponsesMultilingual,
        }: {
            name: string;
            content: string;
            overrideAssessmentData?: AssessmentData;
            autoResponses?: SMSAutoResponse[];
            contentMultilingual?: Record<string, string>;
            autoResponsesMultilingual?: Record<string, SMSAutoResponse[]>;
        }) =>
            handleSaveWithAssessment({
                name,
                content,
                saveMode: SmsSaveMode.SAVE,
                overrideAssessmentData,
                autoResponses,
                contentMultilingual,
                autoResponsesMultilingual,
            }),
        [handleSaveWithAssessment]
    );

    return {
        assessmentData,
        setAssessmentData,
        handleSaveWithAssessment,
        handleSaveAndExitWithAssessment,
        handleSaveNameWithAssessment,
    };
};

export default useAssessmentHandling;
